import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CreditCardIcon, 
  CalendarIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BanknotesIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { policyAPI, formatCurrency, formatDateTime } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Contributions = () => {
  const queryClient = useQueryClient();
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('MOBILE_MONEY');
  const [statusFilter, setStatusFilter] = useState('');
  const [policyFilter, setPolicyFilter] = useState('');

  // Récupérer mes polices pour le filtre
  const { data: policiesData } = useQuery(
    'my-policies-for-contributions',
    () => policyAPI.getUserPolicies()
  );

  const policies = policiesData?.data?.policies || [];

  // Récupérer toutes les cotisations
  const { data: contributionsData, isLoading } = useQuery(
    ['my-contributions', statusFilter, policyFilter],
    () => policyAPI.getUserContributions({ 
      status: statusFilter || undefined,
      policyId: policyFilter || undefined
    }),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
    }
  );

  const contributions = contributionsData?.data?.contributions || [];

  // Ancienne mutation de paiement (désactivée car le paiement est pour l'instant simulé)
  // const payContributionMutation = useMutation(
  //   ({ id, data }) => policyAPI.payContribution(id, data),
  //   {
  //     onSuccess: () => {
  //       toast.success('Paiement effectué avec succès');
  //       queryClient.invalidateQueries('my-contributions');
  //       queryClient.invalidateQueries('my-policies');
  //       setShowPaymentModal(false);
  //       setSelectedContribution(null);
  //     },
  //     onError: (error) => {
  //       toast.error(error.response?.data?.error || 'Erreur lors du paiement');
  //     }
  //   }
  // );

  const handlePayment = (contribution) => {
    setSelectedContribution(contribution);
    setShowPaymentModal(true);
  };

  const handleViewDetails = (contribution) => {
    setSelectedContribution(contribution);
    setShowDetailsModal(true);
  };

  const confirmPayment = () => {
    if (selectedContribution) {
      // Paiement en ligne non encore disponible : afficher un toast d'information
      toast.success('Paiement en ligne - fonctionnalité à venir');
      setShowPaymentModal(false);
      setSelectedContribution(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'OVERDUE':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'PENDING':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'OVERDUE': return 'danger';
      case 'PENDING': return 'warning';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PAID': return 'Payée';
      case 'OVERDUE': return 'En retard';
      case 'PENDING': return 'En attente';
      default: return status;
    }
  };

  const isOverdue = (contribution) => {
    if (contribution.status === 'PAID') return false;
    return new Date(contribution.dueDate) < new Date();
  };

  const getDaysUntilDue = (contribution) => {
    const today = new Date();
    const dueDate = new Date(contribution.dueDate);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Statistiques
  const stats = {
    total: contributions.length,
    paid: contributions.filter(c => c.status === 'PAID').length,
    pending: contributions.filter(c => c.status === 'PENDING').length,
    overdue: contributions.filter(c => c.status === 'OVERDUE' || isOverdue(c)).length,
    totalAmount: contributions.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0),
    paidAmount: contributions.filter(c => c.status === 'PAID').reduce((sum, c) => sum + parseFloat(c.amount || 0), 0)
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Mes cotisations
        </h1>
        <p className="text-gray-600">
          Suivez et payez vos cotisations mensuelles
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total cotisations</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Payées</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.paid}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">En retard</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.overdue}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">XOF</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Montant payé</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(stats.paidAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Statut :</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Tous</option>
              <option value="PENDING">En attente</option>
              <option value="PAID">Payées</option>
              <option value="OVERDUE">En retard</option>
            </select>
          </div>
          <div>
            <label className="form-label">Police :</label>
            <select
              value={policyFilter}
              onChange={(e) => setPolicyFilter(e.target.value)}
              className="form-input"
            >
              <option value="">Toutes les polices</option>
              {policies.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.policyNumber} - {policy.subscription?.offer?.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Liste des cotisations */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {contributions.length === 0 ? (
          <div className="text-center py-12">
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune cotisation</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aucune cotisation trouvée avec les filtres sélectionnés.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Police
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contributions.map((contribution) => {
                  const daysUntilDue = getDaysUntilDue(contribution);
                  const isUrgent = daysUntilDue <= 7 && contribution.status === 'PENDING';
                  
                  return (
                    <tr key={contribution.id} className={`hover:bg-gray-50 ${isUrgent ? 'bg-yellow-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {contribution.policy?.policyNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contribution.policy?.subscription?.offer?.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(contribution.dueDate).toLocaleDateString('fr-FR')}
                        </div>
                        {contribution.status === 'PENDING' && (
                          <div className={`text-xs ${
                            daysUntilDue < 0 ? 'text-red-600' : 
                            daysUntilDue <= 7 ? 'text-yellow-600' : 'text-gray-500'
                          }`}>
                            {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} jours de retard` :
                             daysUntilDue === 0 ? 'Échéance aujourd\'hui' :
                             `Dans ${daysUntilDue} jours`}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(contribution.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(contribution.status)}
                          <span className={`ml-2 badge badge-${getStatusColor(contribution.status?.libelle || contribution.status)}`}>
                            {getStatusLabel(contribution.status?.libelle || contribution.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contribution.paidDate ? 
                          new Date(contribution.paidDate).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {contribution.status === 'PENDING' && (
                            <button
                              onClick={() => handlePayment(contribution)}
                              className="text-green-600 hover:text-green-900"
                              title="Payer"
                            >
                              <CreditCardIcon className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(contribution)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir les détails"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de paiement */}
      {showPaymentModal && selectedContribution && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPaymentModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CreditCardIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Payer une cotisation
                    </h3>
                    <div className="mt-2">
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Police</p>
                            <p className="font-medium">{selectedContribution.policy?.policyNumber}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Échéance</p>
                            <p className="font-medium">{new Date(selectedContribution.dueDate).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Montant</p>
                            <p className="font-medium text-lg text-green-600">{formatCurrency(selectedContribution.amount)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Produit</p>
                            <p className="font-medium">{selectedContribution.policy?.subscription?.offer?.name}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <label className="form-label">Méthode de paiement</label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="form-input"
                        >
                          <option value="MOBILE_MONEY">Mobile Money</option>
                          <option value="BANK_TRANSFER">Virement bancaire</option>
                          <option value="CASH">Espèces</option>
                          <option value="CHECK">Chèque</option>
                        </select>
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note :</strong> Ceci est une simulation. Dans un environnement de production, 
                          vous seriez redirigé vers un système de paiement sécurisé.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmPayment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Payer {formatCurrency(selectedContribution.amount)}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedContribution && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetailsModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Détails de la cotisation
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Informations de la cotisation</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Montant</span>
                        <span className="font-medium">{formatCurrency(selectedContribution.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Date d'échéance</span>
                        <span className="font-medium">{new Date(selectedContribution.dueDate).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Statut</span>
                        <span className={`badge badge-${getStatusColor(selectedContribution.status?.libelle || selectedContribution.status)}`}>
                          {getStatusLabel(selectedContribution.status?.libelle || selectedContribution.status)}
                        </span>
                      </div>
                      {selectedContribution.paidDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Date de paiement</span>
                          <span className="font-medium">{new Date(selectedContribution.paidDate).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                      {selectedContribution.paymentMethod && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Méthode de paiement</span>
                          <span className="font-medium">{selectedContribution.paymentMethod}</span>
                        </div>
                      )}
                      {selectedContribution.transactionRef && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Référence</span>
                          <span className="font-medium font-mono text-xs">{selectedContribution.transactionRef}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Police associée</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Numéro de police</span>
                        <span className="font-medium">{selectedContribution.policy?.policyNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Produit</span>
                        <span className="font-medium">{selectedContribution.policy?.subscription?.offer?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Statut de la police</span>
                        <span className="font-medium">{selectedContribution.policy?.status}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Bénéficiaires</span>
                        <span className="font-medium">{selectedContribution.policy?.beneficiariesCount}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedContribution.status === 'PENDING' && (
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handlePayment(selectedContribution);
                      }}
                      className="btn-primary flex items-center"
                    >
                      <CreditCardIcon className="h-4 w-4 mr-2" />
                      Payer cette cotisation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contributions;
